import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import { Fragment, Key } from "react";
import { ISelectOption } from "src/shared/types/field.types";
import IconComponent from "../../IconComponent";

export type ITableTabsGroupOption<T = string> = ISelectOption<T> &
  Pick<ButtonProps, "startIcon" | "endIcon">;

export interface ITableTabsGroupProps<T> {
  value: unknown;
  buttonsList: ITableTabsGroupOption<T>[];
  onClick: (v: T) => void;
  allowedAgencies?: number[];
}

export function TableTabsGroup<T = string>({
  value,
  buttonsList,
  onClick,
  allowedAgencies,
}: ITableTabsGroupProps<T>) {
  const filteredButtonsList = allowedAgencies
    ? buttonsList.filter(({ value }) => {
        if (value === null) {
          return allowedAgencies.length === 3;
        }

        return allowedAgencies.includes(Number(value));
      })
    : buttonsList;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
      }}
    >
      {filteredButtonsList.map(
        ({ name, value: buttonValue, disabled, iconName, ...icons }) => {
          const isActive: boolean = value == buttonValue;

          return (
            <Fragment key={buttonValue as Key}>
              <Button
                {...icons}
                disabled={disabled}
                fullWidth
                variant="text"
                onClick={() => onClick(buttonValue)}
                sx={{
                  textTransform: "none",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  ["& .icons:only-child"]: { height: "16px", width: "16px" },
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  minWidth: 124,
                  color: "black",
                  opacity: () => (isActive ? 1 : 0.7),
                  padding: (theme) => theme.spacing(2, 4),
                  ["&:after"]: {
                    content: () => (isActive ? "''" : undefined),
                    position: "absolute",
                    display: "block",
                    width: "100%",
                    height: "2px",
                    left: "0",
                    bottom: "0",
                    backgroundColor: "grey.700",
                  },
                  ["&:hover, &:active"]: {
                    opacity: 1,
                  },
                  background: () => {
                    if (!isActive) {
                      return "white";
                    }
                  },
                }}
              >
                {iconName && (
                  <IconComponent
                    sx={{ minWidth: 16, width: 16, height: 16, marginRight: 2 }}
                    iconname={iconName}
                  />
                )}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {name}
                </Typography>
              </Button>
            </Fragment>
          );
        },
      )}
    </Box>
  );
}

export default TableTabsGroup;
